import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { COUNTRIES } from "@/constants/countries";

const PROFESSIONS = [
  "Hairdresser / Hairstylist",
  "Nail Technician",
  "Makeup Artist",
  "Personal Trainer",
  "Massage Therapist",
  "Photographer",
  "Graphic Designer",
  "Social Media Manager",
  "Barber",
  "Videographer",
  "Coach",
  "Accountant",
  "MC (Master of Ceremonies)",
  "Moderator",
  "Other"
] as const;

const SERVICE_TYPES = [
  "Beauty & Wellness",
  "Home Services",
  "Professional Services",
  "Health & Fitness",
  "Education & Tutoring",
  "Tech Services",
  "Other"
] as const;

const PROFESSION_TO_SERVICE_TYPE: Record<string, typeof SERVICE_TYPES[number]> = {
  "Hairdresser / Hairstylist": "Beauty & Wellness",
  "Nail Technician": "Beauty & Wellness",
  "Makeup Artist": "Beauty & Wellness",
  "Personal Trainer": "Health & Fitness",
  "Massage Therapist": "Health & Fitness",
  "Photographer": "Professional Services",
  "Graphic Designer": "Professional Services",
  "Social Media Manager": "Professional Services",
  "Barber": "Beauty & Wellness",
  "Videographer": "Professional Services",
  "Coach": "Education & Tutoring",
  "Accountant": "Professional Services",
  "MC (Master of Ceremonies)": "Professional Services",
  "Moderator": "Professional Services",
};

const REFERRAL_SOURCES = [
  "Gebeya Sales Rep",
  "I saw it on someone's service page",
  "Email",
  "From a friend", 
  "Co-worker",
  "LinkedIn",
  "Twitter", 
  "Instagram",
  "Facebook",
  "TikTok",
  "Other"
] as const;

const PRIORITIZED_COUNTRIES = ["Kenya", "Ethiopia"];

const AMHARIC_NAME_PATTERNS = [
  "abebe", "alemu", "alemayehu", "bekele", "berhanu", "dawit", "desalegn", 
  "ephrem", "eshetu", "fekadu", "getachew", "girma", "haile", "kebede", 
  "kinfe", "melaku", "mekonnen", "melesse", "mengistu", "mulugeta", 
  "solomon", "tadesse", "tefera", "tekle", "teshome", "tilahun", "worku", "yohannes",
  
  "abebech", "almaz", "aregash", "aster", "belaynesh", "bertukan", "bezawit", 
  "eden", "ejigayehu", "elisabet", "emabet", "fatuma", "genet", "hanna", 
  "hirut", "konjit", "lemlem", "marta", "meseret", "mimi", "rahel", "selamawit", 
  "tigist", "tsehay", "yeshimebet", "yeshi", "zewdie", "zufan",

  "addisu", "amare", "andualem", "assefa", "ayele", "biruk", "dagmawi", "demeke",
  "endalkachew", "fasil", "feyisa", "gashaw", "habte", "henok", "kassa", "legesse",
  "mekbib", "meseret", "mulatu", "negussie", "samson", "shimelis", "tesfaye", "wondwossen",
  "yehuala", "yemane", "zerihun", "zinabu",

  "adanech", "adina", "azeb", "bethlehem", "bizunesh", "dagmawit", "deborah", "etenesh",
  "eyerusalem", "ferehiwot", "feven", "gelila", "hawani", "helina", "iman", "kalkidan",
  "kidist", "mahlet", "makeda", "menbere", "messay", "mihret", "nardos", "netsanet",
  "rediet", "selam", "senait", "tadelech", "tinsae", "tizita", "tsion", "winta", "yeabsira",
  "yordanos", "zenebech",

  "abate", "admasu", "asfaw", "asrat", "ayalew", "bayeh", "beyene", "bogale", "chala",
  "dagnachew", "debela", "demissie", "dereje", "desta", "dubale", "ejigu", "endale",
  "fantahun", "gebre", "gebremeskel", "gebremichael", "gebreyesus", "gelan", "gessesse",
  "habtamu", "hadgu", "kassahun", "mamo", "moges", "negash", "seifu", "shiferaw", "taddesse",
  "takele", "tefera", "tessema", "welde", "wolde", "yilma", "zelalem", "zerfu"
];

const isLikelyAmharicName = (name: string): boolean => {
  if (!name) return false;
  const normalizedName = name.toLowerCase().trim();
  
  return AMHARIC_NAME_PATTERNS.some(pattern => 
    normalizedName.includes(pattern)
  );
};

interface BusinessDetailsDialogProps {
  isOpen: boolean;
  isLoading: boolean;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => Promise<void>;
}

export const BusinessDetailsDialog = ({ isOpen, isLoading, onSubmit }: BusinessDetailsDialogProps) => {
  const [selectedProfession, setSelectedProfession] = useState<string | null>(null);
  const [selectedServiceType, setSelectedServiceType] = useState<string | null>(null);
  const [country, setCountry] = useState<string>("Kenya");
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [preferredMessaging, setPreferredMessaging] = useState<string>("whatsapp");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          console.log("Fetching profile for user:", user.id);
          const { data: profile, error } = await supabase
            .from('profiles')
            .select('first_name, last_name, country')
            .eq('id', user.id)
            .single();
            
          if (error) {
            console.error("Error fetching profile:", error);
            return;
          }
          
          console.log("Retrieved profile:", profile);
          
          if (profile.first_name) setFirstName(profile.first_name);
          if (profile.last_name) setLastName(profile.last_name);
          if (profile.country) setCountry(profile.country);
          
          if (profile.country === "Ethiopia") {
            setPreferredMessaging("telegram");
          } else {
            setPreferredMessaging("whatsapp");
          }
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      }
    };
    
    if (isOpen) {
      fetchUserProfile();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isLikelyAmharicName(firstName) || isLikelyAmharicName(lastName)) {
      setCountry("Ethiopia");
    }
  }, [firstName, lastName]);

  useEffect(() => {
    if (country === "Ethiopia") {
      setPreferredMessaging("telegram");
    } else {
      setPreferredMessaging("whatsapp");
    }
  }, [country]);

  const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFirstName = e.target.value;
    setFirstName(newFirstName);
  };

  const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newLastName = e.target.value;
    setLastName(newLastName);
  };

  const sendToHubSpot = async (userData: any) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return true;

      console.log("Sending user data to HubSpot from BusinessDetailsDialog:", userData);
      
      try {
        const { data, error } = await supabase.functions.invoke('hubspot-contact', {
          body: userData
        });

        if (error) {
          console.error("Error sending data to HubSpot:", error);
        } else {
          console.log("HubSpot response:", data);
        }
      } catch (err) {
        console.error("Failed to send data to HubSpot:", err);
      }
      
      return true;
    } catch (err) {
      console.error("Failed to send data to HubSpot:", err);
      return true;
    }
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    setIsSubmitting(true);
    
    try {
      if (!(e.target instanceof HTMLFormElement)) {
        console.error("Form element is not an HTMLFormElement");
        setIsSubmitting(false);
        return;
      }
      
      const formElement = e.target as HTMLFormElement;
      
      if (selectedProfession === "Other") {
        const customProfessionInput = formElement.querySelector('#customProfession') as HTMLInputElement;
        
        if (customProfessionInput && customProfessionInput.value) {
          const hiddenField = document.createElement('input');
          hiddenField.type = 'hidden';
          hiddenField.name = 'company_name';
          hiddenField.value = customProfessionInput.value;
          formElement.appendChild(hiddenField);
        }
      }
      
      if (selectedServiceType) {
        const serviceTypeSelect = formElement.querySelector('select[name="serviceType"]') as HTMLSelectElement;
        
        if ((!serviceTypeSelect || !serviceTypeSelect.value) && selectedServiceType) {
          const hiddenField = document.createElement('input');
          hiddenField.type = 'hidden';
          hiddenField.name = 'serviceType';
          hiddenField.value = selectedServiceType;
          formElement.appendChild(hiddenField);
        }
      }
      
      const countryField = document.createElement('input');
      countryField.type = 'hidden';
      countryField.name = 'country';
      countryField.value = country;
      formElement.appendChild(countryField);
      
      const messagingField = document.createElement('input');
      messagingField.type = 'hidden';
      messagingField.name = 'preferredMessaging';
      messagingField.value = preferredMessaging;
      formElement.appendChild(messagingField);
      
      const formData = new FormData(formElement);
      const selectedProf = formData.get("profession") as string;
      const customProf = formData.get("customProfession") as string;
      const userFirstName = firstName;
      const userLastName = lastName;
      const userCountry = country;
      
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          sendToHubSpot({
            firstName: userFirstName,
            lastName: userLastName,
            email: user.email,
            phone: "",  // Phone is collected in the next step
            profession: selectedProf === "Other" ? customProf : selectedProf,
            country: userCountry
          }).catch(err => {
            console.error('Error in sendToHubSpot:', err);
          });
        }
      } catch (err) {
        console.error('Error sending data to HubSpot from BusinessDetailsDialog:', err);
      }
      
      await onSubmit(e);
    } catch (error) {
      console.error("Error in form submission:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) {
        // If the user tries to close the dialog, handle auth cleanup and redirect
        try {
          // Just redirect back to home
          window.location.href = '/';
        } catch (error) {
          console.error('Error navigating from business details dialog:', error);
        }
      }
    }}>
      <DialogContent className="max-w-md">
        <DialogHeader className="mb-4">
          <DialogTitle className="text-3xl font-bold text-gebeya-pink">
            Tell us about your business
          </DialogTitle>
          <DialogDescription className="text-base text-gray-600">
            Let's get to know your business to help personalize your experience
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleFormSubmit} className="space-y-6" id="business-details-form">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                name="firstName"
                placeholder="Your first name"
                required
                value={firstName}
                onChange={handleFirstNameChange}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                name="lastName"
                placeholder="Your last name"
                required
                value={lastName}
                onChange={handleLastNameChange}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="country">Country</Label>
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  className="w-full justify-between"
                >
                  {country ? country : "Select country..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0">
                <Command>
                  <CommandInput placeholder="Search country..." />
                  <CommandList>
                    <CommandEmpty>No country found.</CommandEmpty>
                    <CommandGroup heading="Recommended">
                      {PRIORITIZED_COUNTRIES.map((prioritizedCountry) => (
                        <CommandItem
                          key={`prioritized-${prioritizedCountry}`}
                          value={prioritizedCountry}
                          onSelect={(currentValue) => {
                            setCountry(currentValue);
                            setOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              country === prioritizedCountry ? "opacity-100" : "opacity-0"
                            )}
                          />
                          {prioritizedCountry}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                    <CommandGroup heading="All Countries">
                      {COUNTRIES.filter(c => !PRIORITIZED_COUNTRIES.includes(c)).map((countryOption) => (
                        <CommandItem
                          key={countryOption}
                          value={countryOption}
                          onSelect={(currentValue) => {
                            setCountry(currentValue);
                            setOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              country === countryOption ? "opacity-100" : "opacity-0"
                            )}
                          />
                          {countryOption}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <input type="hidden" name="country" value={country} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="profession">What's your profession?</Label>
            <Select 
              name="profession" 
              required 
              onValueChange={(value) => {
                setSelectedProfession(value);
                if (value !== "Other") {
                  const mappedServiceType = PROFESSION_TO_SERVICE_TYPE[value];
                  setSelectedServiceType(mappedServiceType);
                } else {
                  setSelectedServiceType(null);
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select your profession" />
              </SelectTrigger>
              <SelectContent position="popper" className="max-h-[300px] overflow-y-auto">
                <SelectGroup>
                  <SelectLabel>Available Professions</SelectLabel>
                  {PROFESSIONS.map((profession) => (
                    <SelectItem key={profession} value={profession}>
                      {profession === "Other" ? <strong>Other</strong> : profession}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            {selectedProfession === "Other" && (
              <div className="mt-2">
                <Input
                  id="customProfession"
                  name="customProfession"
                  placeholder="Enter your profession"
                  required
                />
              </div>
            )}
          </div>

          {selectedProfession === "Other" && (
            <div className="space-y-2">
              <Label htmlFor="serviceType">Type of Service</Label>
              <Select 
                name="serviceType" 
                required
                value={selectedServiceType || undefined}
                onValueChange={setSelectedServiceType}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select your service type" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px] overflow-y-auto">
                  <SelectGroup>
                    <SelectLabel>Service Categories</SelectLabel>
                    {SERVICE_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="referralSource">How did you hear about us?</Label>
            <Select name="referralSource" required>
              <SelectTrigger>
                <SelectValue placeholder="Select how you found us" />
              </SelectTrigger>
              <SelectContent className="max-h-[300px] overflow-y-auto">
                <SelectGroup>
                  <SelectLabel>Referral Sources</SelectLabel>
                  {REFERRAL_SOURCES.map((source) => (
                    <SelectItem key={source} value={source}>
                      {source}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between mt-8">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => window.location.href = '/'}
                className="w-[120px]"
                disabled={isSubmitting || isLoading}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="w-full max-w-[200px] bg-gradient-to-r from-gebeya-pink to-gebeya-orange text-white"
                disabled={isSubmitting || isLoading}
              >
                {isSubmitting || isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </div>
                ) : (
                  'Continue'
                )}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
